import { PLANNING_MEETING_FORM_VERSION } from '../../../constants/general';
import React from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, Grid, Slider } from '@material-ui/core';
import SelectScore from './SelectScore';
import {
  calculateOverallTotalScore,
  getCommunicationOptions,
  getDressedAppropriatelyOptions,
  getModule4CompletedOptions,
  getModule4Point1CompletedOptions,
  getModule4Point2CompletedOptions,
  getModule4SubmittedOptions,
  getModule5CompletedOptions,
  getModule5Point1CompletedOptions,
  getModule5Point2CompletedOptions,
  getModule5Point3CompletedOptions,
  getModule5Point4CompletedOptions,
  getModule5SubmittedOptions,
  getModule6CompletedOptions,
  getModule6Point1CompletedOptions,
  getModule6Point2CompletedOptions,
  getModule6Point3CompletedOptions,
  getModule6Point4CompletedOptions,
  getModule6SubmittedOptions,
  getModule7Point1CompletedOptions,
  getModule7Point2CompletedOptions,
  getModule7Point3CompletedOptions,
  getParticipationOptions,
  getProfessionalJudgmentOptions,
  getSheet2CompletedOptions,
  getSheet2SpeakOptions,
  getSheet2SubmittedOptions,
  getSocialSkillsOptions,
  getWillingnessToAcceptFeedbackOptions,
} from '../../../utils/scoring';
import useScoringStyles from './scoringStyles';

export default function Scoring(props) {
  const { form, setForm, disabled } = props;

  const classes = useScoringStyles();

  const housingRubricMarks = [
    {
      value: 1,
      label: '1',
    },
    {
      value: 2,
      label: '2',
    },
    {
      value: 3,
      label: '3',
    },
    {
      value: 4,
      label: '4',
    },
  ];

  const lookupMotivationValue = (value) => {
    switch (value) {
      case 1:
        return 'Student demonstrates faint interest in working on their post-graduation plan, moving through the motions and completing most basic tasks and expectations with lots of prompts, motivators, and consequences, and showing no initiative or enthusiasm.';
      case 2:
        return 'Student demonstrates some interest in working on the post-graduation plan, completes most basic tasks and expectations with a few prompts, motivators, and consequences, and shows very little initiative or enthusiasm.';
      case 3:
        return 'Student demonstrates interest in working on the post-graduation plan, completes most basic tasks and expectations without prompts, motivators, consequences, has a positive attitude, and shows some initiative and enthusiasm.';
      case 4:
        return 'Student works on their plan with persistence, a positive attitude, self-discipline, and lots of initiative and enthusiasm asking for help and keeping track of all requirements independently.';
      default:
        return '';
    }
  };

  const lookupFutureValue = (value) => {
    switch (value) {
      case 1:
        return 'Student refuses to set a realistic post-graduation plan.';
      case 2:
        return "Student has an unrealistic view of their abilities and post-graduation plans as evidenced by contradictory MAP, ACT, and ASVAB scores, the team's overall unfavorable support, and student is reluctant to feedback received during the Sr. Planning sessions.";
      case 3:
        return 'Student is somewhat realistic about their abilities and post-graduation plans as evidenced by supportive MAP, ACT, and ASVAB scores and team-neutral support, student is open to feedback received during the Sr. Planning sessions.';
      case 4:
        return 'Student is realistic about their abilities and plans for their future as evidenced by supportive MAP, ACT, and ASVAB scores and team overall favorable support.';
      default:
        return '';
    }
  };

  const lookupSFTeamValue = (value) => {
    switch (value) {
      case 1:
        return 'Never: Student does not reach out via email, call, or attend afterschool hours.';
      case 2:
        return 'Sometimes: Student reaches out to SFS only when facing consequences or past due on their requirements.';
      case 3:
        return 'Often: Student reaches out to SFS with proactive questions about their senior planning binder completion and some about their post-graduation plan.';
      case 4:
        return 'Very Often: Student reaches out to SFS with proactive questions about their post-graduation plan and senior planning binder completion. Communication is fluid and allows for relationship building.';
      default:
        return '';
    }
  };

  const handleRubricMotivationSlider = (ev, value) => {
    const explain = lookupMotivationValue(value);
    const avg = (value + form.rubricFuture + form.rubricSFTeam) / 3;
    setForm({ ...form, rubricMotivation: value, rubricMotivationExplain: explain, rubricMotivationScore: avg });
  };

  const handleRubricFutureSlider = (ev, value) => {
    const explain = lookupFutureValue(value);
    const avg = (form.rubricMotivation + value + form.rubricSFTeam) / 3;
    setForm({ ...form, rubricFuture: value, rubricFutureExplain: explain, rubricMotivationScore: avg });
  };

  const handleRubricSFTeamSlider = (ev, value) => {
    const explain = lookupSFTeamValue(value);
    const avg = (form.rubricMotivation + form.rubricFuture + value) / 3;
    setForm({ ...form, rubricSFTeam: value, rubricSFTeamExplain: explain, rubricMotivationScore: avg });
  };

  const isVersionTwo = form.version && form.version === PLANNING_MEETING_FORM_VERSION;

  return (
    <>
      <Grid item xs={12} align="center" style={{ margin: '15px 0' }}>
        <label style={{ fontWeight: 600 }}>Grading the Planning Meeting Form 2</label>
      </Grid>
      <Grid item xs={12} align="left">
        <label style={{ fontWeight: 600 }}>Preparation Sheet #2</label>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginTop: 5 }}>
        <label htmlFor="sheet-2-completed">Sheet is fully completed by student.</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <SelectScore
          labelId="sheet-2-fully-completed"
          id="sheet-2-completed"
          data-testid="sheet-2-completed"
          value={form.gradingSheet2Completed}
          onChange={(event) => setForm({ ...form, gradingSheet2Completed: event.target.value })}
          disabled={disabled}
          items={getSheet2CompletedOptions(form.version)}
        />
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label htmlFor="sheet-2-submitted" id="sheet-2-submitted-on-time-label">
          Sheet is submitted on time.
        </label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <SelectScore
          labelId="sheet-2-submitted-label"
          id="sheet-2-submitted"
          data-testid="sheet-2-submitted"
          value={form.gradingSheet2Submitted}
          onChange={(event) => setForm({ ...form, gradingSheet2Submitted: event.target.value })}
          disabled={disabled}
          items={getSheet2SubmittedOptions(form.version)}
        />
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      {!isVersionTwo && (
        <>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 10 }}>
            <label>Student is able to clearly speak about her/his plan options and rationales.</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 10 }}>
            <SelectScore
              labelId="sheet-2-clarity-label"
              id="sheet-2-clarity"
              data-testid="sheet-2-clarity"
              value={form.gradingSheet2Clarity}
              onChange={(event) => setForm({ ...form, gradingSheet2Clarity: event.target.value })}
              disabled={disabled}
              items={getSheet2SpeakOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      {isVersionTwo && (
        <>
          {/* Overall Social Skills */}
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label htmlFor="sheet-2-socialSkills">Overall Social Skills.</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="sheet-2-socialSkills"
              id="sheet-2-socialSkills"
              data-testid="social-skills"
              value={typeof form.socialSkills === 'number' ? form.socialSkills : -1}
              onChange={(event) => setForm({ ...form, socialSkills: event.target.value })}
              disabled={disabled}
              items={getSocialSkillsOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          {/* Dressed Appropriately */}
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label htmlFor="dressed-appropriately">Dressed Appropriately</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="dressed-appropriately"
              id="dressed-appropriately"
              data-testid="dressed-appropriately"
              value={typeof form.dressedAppropriately === 'number' ? form.dressedAppropriately : -1}
              onChange={(event) => setForm({ ...form, dressedAppropriately: event.target.value })}
              disabled={disabled}
              items={getDressedAppropriatelyOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          {/* Communication */}
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label htmlFor="communication">Communication</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="communication"
              id="communication"
              data-testid="communication"
              value={typeof form.communication === 'number' ? form.communication : -1}
              onChange={(event) => setForm({ ...form, communication: event.target.value })}
              disabled={disabled}
              items={getCommunicationOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          {/* Participation */}
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label htmlFor="participation">Participation</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="participation"
              id="participation"
              data-testid="participation"
              value={typeof form.participation === 'number' ? form.participation : -1}
              onChange={(event) => setForm({ ...form, participation: event.target.value })}
              disabled={disabled}
              items={getParticipationOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          {/* Willingness to Accept Feedback */}
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label htmlFor="willingnessFeedback"> Willingness to Accept Feedback</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="willingnessFeedback"
              id="willingnessFeedback"
              data-testid="willingnessFeedback"
              value={typeof form.willingnessFeedback === 'number' ? form.willingnessFeedback : -1}
              onChange={(event) => setForm({ ...form, willingnessFeedback: event.target.value })}
              disabled={disabled}
              items={getWillingnessToAcceptFeedbackOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: 600 }}>Preparation Sheet #2 Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {isVersionTwo
          ? [
              'gradingSheet2Completed',
              'gradingSheet2Submitted',
              'socialSkills',
              'dressedAppropriately',
              'communication',
              'participation',
              'willingnessFeedback',
            ]
              .map((key) => (typeof form[key] === 'number' && form[key] >= 0 ? form[key] : 0))
              .reduce((sum, val) => sum + val, 0)
          : ['gradingSheet2Completed', 'gradingSheet2Submitted', 'gradingSheet2Clarity']
              .map((key) => (typeof form[key] === 'number' && form[key] >= 0 ? form[key] : 0))
              .reduce((sum, val) => sum + val, 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label style={{ fontWeight: 600 }}>Module #4</label>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>{isVersionTwo ? 'Modules were fully completed on time' : 'Module was submitted on time.'}</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <SelectScore
          labelId="module-4-submitted-label"
          id="module-4-submitted"
          data-testid="module-4-submitted"
          value={form.module4Submitted}
          onChange={(event) => setForm({ ...form, module4Submitted: event.target.value })}
          disabled={disabled}
          items={getModule4SubmittedOptions(form.version)}
        />
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      {!isVersionTwo && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 35 }}>
            <label>
              Worksheets and activities (housing questions, family contract, if applicable, transportation options, affording a car, etc.)
              thoughtfully completed with realistic exploration of options and clear notes and follow-up descriptions.
            </label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 35 }}>
            <SelectScore
              labelId="module-4-completed-label"
              id="module-4-completed"
              data-testid="module-4-completed"
              value={form.module4Completed}
              onChange={(event) => setForm({ ...form, module4Completed: event.target.value })}
              disabled={disabled}
              items={getModule4CompletedOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}
      {isVersionTwo && (
        <>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 4.1 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-4-1-completed-label"
              id="module-4-1-completed"
              data-testid="module-4-1-completed"
              value={form.module4Point1Completed ?? -1}
              onChange={(event) => setForm({ ...form, module4Point1Completed: event.target.value })}
              disabled={disabled}
              items={getModule4Point1CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 4.2 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-4-2-completed-label"
              id="module-4-2-completed"
              data-testid="module-4-2-completed"
              value={form.module4Point2Completed ?? -1}
              onChange={(event) => setForm({ ...form, module4Point2Completed: event.target.value })}
              disabled={disabled}
              items={getModule4Point2CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: 600 }}>Module #4 Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {isVersionTwo
          ? (form.module4Submitted && form.module4Submitted >= 0 ? form.module4Submitted : 0) +
            (form.module4Point1Completed && form.module4Point1Completed >= 0 ? form.module4Point1Completed : 0) +
            (form.module4Point2Completed && form.module4Point2Completed >= 0 ? form.module4Point2Completed : 0)
          : (form.module4Submitted && form.module4Submitted >= 0 ? form.module4Submitted : 0) +
            (form.module4Completed && form.module4Completed >= 0 ? form.module4Completed : 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label style={{ fontWeight: 600 }}>Module #5</label>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>{isVersionTwo ? 'Modules were fully completed on time' : 'Module was submitted on time.'}</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <SelectScore
          labelId="module-5-submitted-label"
          id="module-5-submitted"
          data-testid="module-5-submitted"
          value={form.module5Submitted}
          onChange={(event) => setForm({ ...form, module5Submitted: event.target.value })}
          disabled={disabled}
          items={getModule5SubmittedOptions(form.version)}
        />
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      {!isVersionTwo && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            <label>
              Health Worksheet and activities (insurance, healthy lifestyle questions, SODAs etc.) thoughtfully completed with realistic exploration
              of options and clear notes and follow-up descriptions.
            </label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            <SelectScore
              labelId="module-5-completed-label"
              id="module-5-completed"
              data-testid="module-5-completed"
              value={form.module5Completed}
              onChange={(event) => setForm({ ...form, module5Completed: event.target.value })}
              disabled={disabled}
              items={getModule5CompletedOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      {isVersionTwo && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 5.1 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-5-1-completed-label"
              id="module-5-1-completed"
              data-testid="module-5-1-completed"
              value={form.module5Point1Completed ?? -1}
              onChange={(event) => setForm({ ...form, module5Point1Completed: event.target.value })}
              disabled={disabled}
              items={getModule5Point1CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 5.2 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-5-2-completed-label"
              id="module-5-2-completed"
              data-testid="module-5-2-completed"
              value={form.module5Point2Completed ?? -1}
              onChange={(event) => setForm({ ...form, module5Point2Completed: event.target.value })}
              disabled={disabled}
              items={getModule5Point2CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 5.3 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-5-3-completed-label"
              id="module-5-3-completed"
              data-testid="module-5-3-completed"
              value={form.module5Point3Completed ?? -1}
              onChange={(event) => setForm({ ...form, module5Point3Completed: event.target.value })}
              disabled={disabled}
              items={getModule5Point3CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 5.4 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-5-4-completed-label"
              id="module-5-4-completed"
              data-testid="module-5-4-completed"
              value={form.module5Point4Completed ?? -1}
              onChange={(event) => setForm({ ...form, module5Point4Completed: event.target.value })}
              disabled={disabled}
              items={getModule5Point4CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: 600 }}>Module #5 Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {isVersionTwo
          ? (form.module5Submitted && form.module5Submitted >= 0 ? form.module5Submitted : 0) +
            (form.module5Point1Completed && form.module5Point1Completed >= 0 ? form.module5Point1Completed : 0) +
            (form.module5Point2Completed && form.module5Point2Completed >= 0 ? form.module5Point2Completed : 0) +
            (form.module5Point3Completed && form.module5Point3Completed >= 0 ? form.module5Point3Completed : 0) +
            (form.module5Point4Completed && form.module5Point4Completed >= 0 ? form.module5Point4Completed : 0)
          : (form.module5Submitted && form.module5Submitted >= 0 ? form.module5Submitted : 0) +
            (form.module5Completed && form.module5Completed >= 0 ? form.module5Completed : 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      <Grid item xs={12} align="left">
        <label style={{ fontWeight: 600 }}>Module #6</label>
      </Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label>{isVersionTwo ? 'Modules were fully completed on time' : 'Module was submitted on time.'}</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <SelectScore
          labelId="module-6-submitted-label"
          id="module-6-submitted"
          data-testid="module-6-submitted"
          value={form.module6Submitted}
          onChange={(event) => setForm({ ...form, module6Submitted: event.target.value })}
          disabled={disabled}
          items={getModule6SubmittedOptions(form.version)}
        />
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      {!isVersionTwo && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 40 }}>
            <label>
              Worksheets (spirituality/faith, personal boundaries, community resources, etc.) and activities (friends, relationships with adults,
              support systems, time management, etc.) thoughtfully completed with realistic exploration of options and clear notes and follow-up
              descriptions.
            </label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            <SelectScore
              labelId="module-6-completed-label"
              id="module-6-completed"
              data-testid="module-6-completed"
              value={form.module6Completed}
              onChange={(event) => setForm({ ...form, module6Completed: event.target.value })}
              disabled={disabled}
              items={getModule6CompletedOptions()}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      {isVersionTwo && (
        <>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 6.1 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-6-1-completed-label"
              id="module-6-1-completed"
              data-testid="module-6-1-completed"
              value={form.module6Point1Completed ?? -1}
              onChange={(event) => setForm({ ...form, module6Point1Completed: event.target.value })}
              disabled={disabled}
              items={getModule6Point1CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 6.2 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-6-2-completed-label"
              id="module-6-2-completed"
              data-testid="module-6-2-completed"
              value={form.module6Point2Completed ?? -1}
              onChange={(event) => setForm({ ...form, module6Point2Completed: event.target.value })}
              disabled={disabled}
              items={getModule6Point2CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 6.3 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-6-3-completed-label"
              id="module-6-3-completed"
              data-testid="module-6-3-completed"
              value={form.module6Point3Completed ?? -1}
              onChange={(event) => setForm({ ...form, module6Point3Completed: event.target.value })}
              disabled={disabled}
              items={getModule6Point3CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 6.4 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-6-4-completed-label"
              id="module-6-4-completed"
              data-testid="module-6-4-completed"
              value={form.module6Point4Completed ?? -1}
              onChange={(event) => setForm({ ...form, module6Point4Completed: event.target.value })}
              disabled={disabled}
              items={getModule6Point4CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: 600 }}>Module #6 Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
        {isVersionTwo
          ? (form.module6Submitted && form.module6Submitted >= 0 ? form.module6Submitted : 0) +
            (form.module6Point1Completed && form.module6Point1Completed >= 0 ? form.module6Point1Completed : 0) +
            (form.module6Point2Completed && form.module6Point2Completed >= 0 ? form.module6Point2Completed : 0) +
            (form.module6Point3Completed && form.module6Point3Completed >= 0 ? form.module6Point3Completed : 0) +
            (form.module6Point4Completed && form.module6Point4Completed >= 0 ? form.module6Point4Completed : 0)
          : (form.module6Submitted && form.module6Submitted >= 0 ? form.module6Submitted : 0) +
            (form.module6Completed && form.module6Completed >= 0 ? form.module6Completed : 0)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      {/*MODULE #7 STARTS*/}
      {isVersionTwo && (
        <>
          <Grid item xs={12} align="left">
            <label style={{ fontWeight: 600 }}>Module #7</label>
          </Grid>
          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 7.1 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-7-1-completed-label"
              id="module-7-1-completed"
              data-testid="module-7-1-completed"
              value={form.module7Point1Completed ?? -1}
              onChange={(event) => setForm({ ...form, module7Point1Completed: event.target.value })}
              disabled={disabled}
              items={getModule7Point1CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 7.1 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-7-2-completed-label"
              id="module-7-2-completed"
              data-testid="module-7-2-completed"
              value={form.module7Point2Completed ?? -1}
              onChange={(event) => setForm({ ...form, module7Point2Completed: event.target.value })}
              disabled={disabled}
              items={getModule7Point2CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} align="left"></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <label>Module 7.3 Activity Completion</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
            <SelectScore
              labelId="module-7-3-completed-label"
              id="module-7-3-completed"
              data-testid="module-7-3-completed"
              value={form.module7Point3Completed ?? -1}
              onChange={(event) => setForm({ ...form, module7Point3Completed: event.target.value })}
              disabled={disabled}
              items={getModule7Point3CompletedOptions(form.version)}
            />
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            <label style={{ fontWeight: 600 }}>Module #7 Score</label>
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
          <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left" style={{ marginBottom: 20 }}>
            {(form.module7Point1Completed && form.module7Point1Completed >= 0 ? form.module7Point1Completed : 0) +
              (form.module7Point2Completed && form.module7Point2Completed >= 0 ? form.module7Point2Completed : 0) +
              (form.module7Point3Completed && form.module7Point3Completed >= 0 ? form.module7Point3Completed : 0)}
          </Grid>
          <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
        </>
      )}
      {/* MODULE #7 ENDS*/}

      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        <label style={{ fontWeight: 600 }}>Overall Total Score</label>
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>
      <Grid item xs={12} sm={12} md={5} lg={5} className={classes.textRowSpacer} align="left">
        {calculateOverallTotalScore(form)}
      </Grid>
      <Grid item xs={false} sm={false} md={1} lg={1}></Grid>

      {!isVersionTwo && (
        <>
          <Grid container direction="row">
            <Grid item xs={12} align="center" style={{ margin: '25px 0 15px 0' }}>
              <div className={classes.rubricTitle}>Housing Rubric Enhancement</div>
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
              1. Displays self-motivation while working on their post-graduation plan (beyond the actual completion of the senior planner modules).
            </Grid>
            <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
              <FormControl fullWidth>
                <Box sx={{ width: 300 }}>
                  <Slider
                    aria-label="motivation-rubric"
                    value={form.rubricMotivation}
                    onChange={handleRubricMotivationSlider}
                    marks={housingRubricMarks}
                    step={1}
                    min={0}
                    max={4}
                  />
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
              {form.rubricMotivationExplain && form.rubricMotivationExplain.length > 0 && form.rubricMotivationExplain}
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
              2. Thinking about their future (SFS)
            </Grid>
            <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
              <FormControl fullWidth>
                <Box sx={{ width: 300 }}>
                  <Slider
                    aria-label="future-rubric"
                    value={form.rubricFuture}
                    onChange={handleRubricFutureSlider}
                    marks={housingRubricMarks}
                    step={1}
                    min={0}
                    max={4}
                  />
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
              {form.rubricFutureExplain && form.rubricFutureExplain.length > 0 && form.rubricFutureExplain}
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
              3. Student communication with SF team: Student is appropriately communicating (email, call, afterschool hours) with SF
            </Grid>
            <Grid item xs={8} sm={6} md={3} lg={4} className={classes.textRowSpacer} align="left">
              <FormControl fullWidth>
                <Box sx={{ width: 300 }}>
                  <Slider
                    aria-label="sfteam-rubric"
                    value={form.rubricSFTeam}
                    onChange={handleRubricSFTeamSlider}
                    marks={housingRubricMarks}
                    step={1}
                    min={0}
                    max={4}
                  />
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricExplanation} align="left">
              {form.rubricSFTeamExplain && form.rubricSFTeamExplain.length > 0 && form.rubricSFTeamExplain}
            </Grid>
          </Grid>

          <Grid container direction="row">
            <Grid item xs={12} sm={12} md={12} lg={12} className={classes.rubricQuestion} align="left">
              4. Considering all available collateral information and professional judgment, as an SFS, how likely is this student to succeed with
              their current post-graduation plan?
            </Grid>
            <Grid item xs={6} sm={5} md={4} lg={3} className={classes.textRowSpacer} align="left">
              <SelectScore
                variant="outlined"
                labelId="likely-success-label"
                id="likely-success"
                data-testid="professional-judgment"
                value={form.rubricSuccess ?? 0}
                onChange={(event) => setForm({ ...form, rubricSuccess: event.target.value })}
                disabled={disabled}
                items={getProfessionalJudgmentOptions()}
              />
            </Grid>
          </Grid>

          <div style={{ padding: '75px 0 10px 0', width: '100%' }}>
            <Grid container direction="row">
              <Grid item xs={9} sm={8} md={5} lg={3} className={classes.rubricTitle} align="left">
                Motivation Score
              </Grid>
              <Grid item xs={6} sm={5} md={4} lg={3} className={classes.rubricScore} align="left">
                {form.rubricMotivationScore > 0 ? form.rubricMotivationScore.toFixed(2) : 0}
              </Grid>
              <Grid item xs={false} sm={false} md={5} lg={7}></Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item xs={9} sm={8} md={5} lg={3} className={classes.rubricTitle} align="left">
                Likely to Succeed Score
              </Grid>
              <Grid item xs={3} sm={4} md={2} lg={2} className={classes.rubricScore} align="left">
                {form.rubricSuccess}
              </Grid>
              <Grid item xs={false} sm={false} md={5} lg={7}></Grid>
            </Grid>
          </div>
        </>
      )}
    </>
  );
}

Scoring.propTypes = {
  form: PropTypes.object,
  setForm: PropTypes.func,
  disabled: PropTypes.bool,
};
